import { useMemo } from "react";

export function useEBITDACalcs({
  // Revenue arrays (from other hooks)
  valoresMensuales,
  valoresMensualesAnoAnterior,
  ingresosHardwareMensual,
  ingresosHardwareMensualAnoAnterior,
  ingresosHardwareMensualChile,
  ingresosHardwareMensualPeru,
  ingresosGestionContenidosMensual,
  ingresosGestionContenidosMensualAnoAnterior,
  ingresosBTLMensual,
  ingresosBTLMensualAnoAnterior,
  ingresosBTLMensualChile,
  ingresosBTLMensualPeru,
  // Commission arrays (from publicidad hook)
  valoresMensualesComisionAgencia,
  valoresMensualesComisionAgenciaAnoAnterior,
  valoresMensualesComisiones,
  valoresMensualesComisionesAnoAnterior,
  valoresMensualesComisionComercial,
  valoresMensualesComisionComercialAnoAnterior,
  // Hardware costs (from hardware hook)
  compraHardwareMensual,
  instalacionMensual,
  materialesMensual,
  otrosCostosMensual,
  compraHardwareMensualAnoAnterior,
  instalacionMensualAnoAnterior,
  materialesMensualAnoAnterior,
  otrosCostosMensualAnoAnterior,
  // BTL costs (from BTL hook)
  comisionEdificiosBTLMensual,
  otrosCostosBTLMensual,
  comisionEdificiosBTLMensualAnoAnterior,
  otrosCostosBTLMensualAnoAnterior,
  // Gestion/manual costs functions (from index.js)
  getCostoGestionValue,
  getCostoGestionValueAnoAnterior,
  calcularCostoSoftwareEstimado,
  getValueForMonth,
  conceptosManuales,
  costosPublicidadDataAnoAnterior,
  // Fixed costs (from costosFijos hook)
  sumarCostosFijosMes,
  costosFijosMensualAnoAnterior,
  costosFijosMensualesCombinados,
  // Combinados arrays (from other hooks or index.js)
  publicidadMensualesCombinados,
  gestionMensualesCombinados,
  costosVariablesMensualesCombinados,
  // Depreciation (from index.js)
  depreciacionMensualChile,
  depreciacionMensualPeru,
  // Exchange rates
  dolarMensual,
  dolarMensualAnoAnterior,
  dolarMensualChile,
  dolarMensualPeru,
  // Config
  monthsToShow,
  selectedYear,
  currentYear,
  modoCombinado,
  pais,
  verEnDolares,
  dolarFallback,
}) {
  // Facturación Mensual (array centralizado)
  const facturacionMensual = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) =>
          i >= monthsToShow
            ? 0
            : (valoresMensuales[i] || 0) +
              (ingresosHardwareMensual[i] || 0) +
              (ingresosGestionContenidosMensual[i] || 0) +
              (ingresosBTLMensual[i] || 0)
        ),
    [valoresMensuales, ingresosHardwareMensual, ingresosGestionContenidosMensual, ingresosBTLMensual, monthsToShow]
  );

  // Facturación Mensual AÑO ANTERIOR
  const facturacionMensualAnoAnterior = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map(
          (_, i) =>
            (valoresMensualesAnoAnterior[i] || 0) +
            (ingresosHardwareMensualAnoAnterior[i] || 0) +
            (ingresosGestionContenidosMensualAnoAnterior[i] || 0) +
            (ingresosBTLMensualAnoAnterior[i] || 0)
        ),
    [
      valoresMensualesAnoAnterior,
      ingresosHardwareMensualAnoAnterior,
      ingresosGestionContenidosMensualAnoAnterior,
      ingresosBTLMensualAnoAnterior,
    ]
  );

  // Costos Variables Mensual - con fallback de software estimado (lógica correcta)
  const costosVariablesMensual = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) => {
          if (i >= monthsToShow) return 0;
          const softwareSupabase = getCostoGestionValue(i, "software_ds") || 0;
          const softwareUsado = softwareSupabase > 0 ? softwareSupabase : calcularCostoSoftwareEstimado(i);
          const comAg = valoresMensualesComisionAgencia[i] || 0;
          const comEd = valoresMensualesComisiones[i] || 0;
          const comCom = valoresMensualesComisionComercial.valores[i] || 0;
          const manuales = conceptosManuales.reduce((sum, c) => sum + (getValueForMonth(c, i + 1).value || 0), 0);
          const hw =
            (compraHardwareMensual[i] || 0) +
            (instalacionMensual[i] || 0) +
            (materialesMensual[i] || 0) +
            (otrosCostosMensual[i] || 0);
          const otrosGestion = getCostoGestionValue(i, "otros") || 0;
          const btl = (comisionEdificiosBTLMensual[i] || 0) + (otrosCostosBTLMensual[i] || 0);
          return comAg + comEd + comCom + manuales + hw + softwareUsado + otrosGestion + btl;
        }),
    [
      monthsToShow,
      valoresMensualesComisionAgencia,
      valoresMensualesComisiones,
      valoresMensualesComisionComercial,
      conceptosManuales,
      getValueForMonth,
      compraHardwareMensual,
      instalacionMensual,
      materialesMensual,
      otrosCostosMensual,
      getCostoGestionValue,
      calcularCostoSoftwareEstimado,
      comisionEdificiosBTLMensual,
      otrosCostosBTLMensual,
    ]
  );

  // Costos Variables Mensual AÑO ANTERIOR
  const costosVariablesMensualAnoAnterior = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) => {
          // Conceptos manuales del año anterior (Servicios internet, Software DS pub, Mantenimiento)
          // Software DS: restar costo cartelería (igual que getSoftwareDSValueA) para evitar doble conteo
          // con getCostoGestionValueAnoAnterior(software_ds) que se suma más abajo
          const cpAA = costosPublicidadDataAnoAnterior[i] || {};
          const rawSoftwareDS = Number(cpAA.software_ds) || 0;
          const costoCarteleria = calcularCostoSoftwareEstimado(i);
          const softwareDSAjustado = Math.max(0, rawSoftwareDS - costoCarteleria);
          const conceptosManualesAA =
            (Number(cpAA.servicios_internet) || 0) + softwareDSAjustado + (Number(cpAA.mantenimiento) || 0);
          const comAg = valoresMensualesComisionAgenciaAnoAnterior[i] || 0;
          const comEd = valoresMensualesComisionesAnoAnterior[i] || 0;
          const comCom = valoresMensualesComisionComercialAnoAnterior[i] || 0;
          const hw =
            (compraHardwareMensualAnoAnterior[i] || 0) +
            (instalacionMensualAnoAnterior[i] || 0) +
            (materialesMensualAnoAnterior[i] || 0) +
            (otrosCostosMensualAnoAnterior[i] || 0);
          const swGestion = getCostoGestionValueAnoAnterior(i, "software_ds") || 0;
          const otrosGestion = getCostoGestionValueAnoAnterior(i, "otros") || 0;
          const btl = (comisionEdificiosBTLMensualAnoAnterior[i] || 0) + (otrosCostosBTLMensualAnoAnterior[i] || 0);
          return comAg + comEd + comCom + conceptosManualesAA + hw + swGestion + otrosGestion + btl;
        }),
    [
      costosPublicidadDataAnoAnterior,
      calcularCostoSoftwareEstimado,
      valoresMensualesComisionAgenciaAnoAnterior,
      valoresMensualesComisionesAnoAnterior,
      valoresMensualesComisionComercialAnoAnterior,
      compraHardwareMensualAnoAnterior,
      instalacionMensualAnoAnterior,
      materialesMensualAnoAnterior,
      otrosCostosMensualAnoAnterior,
      getCostoGestionValueAnoAnterior,
      comisionEdificiosBTLMensualAnoAnterior,
      otrosCostosBTLMensualAnoAnterior,
    ]
  );

  // EBITDA Mensual (array centralizado)
  const ebitdaMensual = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) =>
          i >= monthsToShow ? 0 : facturacionMensual[i] - costosVariablesMensual[i] - sumarCostosFijosMes(i)
        ),
    [facturacionMensual, costosVariablesMensual, sumarCostosFijosMes, monthsToShow]
  );

  // EBITDA Mensual AÑO ANTERIOR
  const ebitdaMensualAnoAnterior = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map(
          (_, i) =>
            facturacionMensualAnoAnterior[i] - costosVariablesMensualAnoAnterior[i] - costosFijosMensualAnoAnterior[i]
        ),
    [facturacionMensualAnoAnterior, costosVariablesMensualAnoAnterior, costosFijosMensualAnoAnterior]
  );

  // Hardware combinado en USD (usa valores FIJOS de Chile y Perú)
  const hardwareMensualesCombinados = useMemo(() => {
    if (!modoCombinado) return null;

    const resultado = Array(12).fill(0);
    for (let i = 0; i < 12; i++) {
      // Usar valores FIJOS de Chile y Perú (no depende de sesión)
      const hardwareChile = ingresosHardwareMensualChile[i] || 0;
      const hardwarePeru = ingresosHardwareMensualPeru[i] || 0;

      // Convertir usando dólares FIJOS
      const chileUSD = hardwareChile / (dolarMensualChile[i] || dolarFallback.Chile);
      const peruUSD = hardwarePeru / (dolarMensualPeru[i] || dolarFallback["Perú"]);

      resultado[i] = chileUSD + peruUSD;
    }
    return resultado;
  }, [modoCombinado, ingresosHardwareMensualChile, ingresosHardwareMensualPeru, dolarMensualChile, dolarMensualPeru]);

  // BTL combinado en USD (usa valores FIJOS de Chile y Perú)
  const btlMensualesCombinados = useMemo(() => {
    if (!modoCombinado) return null;

    const resultado = Array(12).fill(0);
    for (let i = 0; i < 12; i++) {
      // Usar valores FIJOS de Chile y Perú (no depende de sesión)
      const btlChile = ingresosBTLMensualChile[i] || 0;
      const btlPeru = ingresosBTLMensualPeru[i] || 0;

      // Convertir usando dólares FIJOS
      const chileUSD = btlChile / (dolarMensualChile[i] || dolarFallback.Chile);
      const peruUSD = btlPeru / (dolarMensualPeru[i] || dolarFallback["Perú"]);

      resultado[i] = chileUSD + peruUSD;
    }
    return resultado;
  }, [modoCombinado, ingresosBTLMensualChile, ingresosBTLMensualPeru, dolarMensualChile, dolarMensualPeru]);

  // Depreciación mensual combinada en USD
  const depreciacionMensualesCombinados = useMemo(() => {
    if (!modoCombinado) return null;

    const resultado = Array(12).fill(0);
    for (let i = 0; i < 12; i++) {
      const depChile = depreciacionMensualChile[i] || 0;
      const depPeru = depreciacionMensualPeru[i] || 0;

      const chileUSD = depChile / (dolarMensualChile[i] || dolarFallback.Chile);
      const peruUSD = depPeru / (dolarMensualPeru[i] || dolarFallback["Perú"]);

      resultado[i] = chileUSD + peruUSD;
    }
    return resultado;
  }, [modoCombinado, depreciacionMensualChile, depreciacionMensualPeru, dolarMensualChile, dolarMensualPeru]);

  // EBITDA Run Rate (últimos 3 meses) - usa arrays centralizados
  const ebitdaUltimos3 = useMemo(() => {
    let suma = 0;
    for (let offset = 0; offset < 3; offset++) {
      const mesRelativo = monthsToShow - 1 - offset;
      if (mesRelativo >= 0) {
        suma += ebitdaMensual[mesRelativo] || 0;
      } else {
        const mesAnoAnterior = 12 + mesRelativo;
        suma += ebitdaMensualAnoAnterior[mesAnoAnterior] || 0;
      }
    }
    return suma;
  }, [monthsToShow, ebitdaMensual, ebitdaMensualAnoAnterior]);

  // Run Rate Facturación (últimos 3 meses) - considera cruce de años
  const factUltimos3 = useMemo(() => {
    let suma = 0;
    // Calcular los últimos 3 meses reales (pueden cruzar año)
    for (let offset = 0; offset < 3; offset++) {
      const mesRelativo = monthsToShow - 1 - offset; // 0, -1, -2 respecto al mes actual
      if (mesRelativo >= 0) {
        // Mes del año actual
        suma +=
          (valoresMensuales[mesRelativo] || 0) +
          (ingresosHardwareMensual[mesRelativo] || 0) +
          (ingresosGestionContenidosMensual[mesRelativo] || 0) +
          (ingresosBTLMensual[mesRelativo] || 0);
      } else {
        // Mes del año anterior (índice 12 + mesRelativo: -1 -> 11, -2 -> 10)
        const mesAnoAnterior = 12 + mesRelativo;
        suma +=
          (valoresMensualesAnoAnterior[mesAnoAnterior] || 0) +
          (ingresosHardwareMensualAnoAnterior[mesAnoAnterior] || 0) +
          (ingresosGestionContenidosMensualAnoAnterior[mesAnoAnterior] || 0) +
          (ingresosBTLMensualAnoAnterior[mesAnoAnterior] || 0);
      }
    }
    return suma;
  }, [
    monthsToShow,
    valoresMensuales,
    valoresMensualesAnoAnterior,
    ingresosHardwareMensual,
    ingresosHardwareMensualAnoAnterior,
    ingresosGestionContenidosMensual,
    ingresosGestionContenidosMensualAnoAnterior,
    ingresosBTLMensual,
    ingresosBTLMensualAnoAnterior,
  ]);

  const runRateAnual = Math.round(factUltimos3 * 4);

  // Facturación últimos 12 meses (ventana móvil) para comparar con Run Rate
  // Ej: si estamos en Ene 2026, incluye Feb 2025 → Ene 2026
  const facturacionUltimos12Meses = useMemo(() => {
    let suma = 0;
    // Meses del año actual (0 a monthsToShow-1)
    for (let i = 0; i < monthsToShow; i++) {
      suma +=
        (valoresMensuales[i] || 0) +
        (ingresosHardwareMensual[i] || 0) +
        (ingresosGestionContenidosMensual[i] || 0) +
        (ingresosBTLMensual[i] || 0);
    }
    // Meses del año anterior (monthsToShow a 11)
    for (let i = monthsToShow; i < 12; i++) {
      suma +=
        (valoresMensualesAnoAnterior[i] || 0) +
        (ingresosHardwareMensualAnoAnterior[i] || 0) +
        (ingresosGestionContenidosMensualAnoAnterior[i] || 0) +
        (ingresosBTLMensualAnoAnterior[i] || 0);
    }
    return suma;
  }, [
    monthsToShow,
    valoresMensuales,
    valoresMensualesAnoAnterior,
    ingresosHardwareMensual,
    ingresosHardwareMensualAnoAnterior,
    ingresosGestionContenidosMensual,
    ingresosGestionContenidosMensualAnoAnterior,
    ingresosBTLMensual,
    ingresosBTLMensualAnoAnterior,
  ]);

  // EBITDA últimos 12 meses (ventana móvil) - usa arrays centralizados
  const ebitdaUltimos12Meses = useMemo(() => {
    let suma = 0;
    // Meses del año actual (0 a monthsToShow-1)
    for (let i = 0; i < monthsToShow; i++) {
      suma += ebitdaMensual[i] || 0;
    }
    // Meses del año anterior (monthsToShow a 11)
    for (let i = monthsToShow; i < 12; i++) {
      suma += ebitdaMensualAnoAnterior[i] || 0;
    }
    return suma;
  }, [monthsToShow, ebitdaMensual, ebitdaMensualAnoAnterior]);

  const ebitdaRunRateAnual = Math.round(ebitdaUltimos3 * 4);

  // Run Rate en USD (para header cuando verEnDolares está activo)
  // Usa los mismos arrays centralizados que factUltimos3, con cruce de año
  const runRateAnualUSD = useMemo(() => {
    let sumaDolares = 0;
    for (let offset = 0; offset < 3; offset++) {
      const mesRelativo = monthsToShow - 1 - offset;
      if (mesRelativo >= 0) {
        const factMes = facturacionMensual[mesRelativo] || 0;
        const tc = dolarMensual[mesRelativo] || dolarFallback[pais] || 1;
        sumaDolares += factMes / tc;
      } else {
        const mesAnoAnterior = 12 + mesRelativo;
        const factMes = facturacionMensualAnoAnterior[mesAnoAnterior] || 0;
        const tc = dolarMensualAnoAnterior[mesAnoAnterior] || dolarFallback[pais] || 1;
        sumaDolares += factMes / tc;
      }
    }
    return Math.round(sumaDolares * 4);
  }, [
    monthsToShow,
    facturacionMensual,
    facturacionMensualAnoAnterior,
    dolarMensual,
    dolarMensualAnoAnterior,
    dolarFallback,
    pais,
  ]);

  // EBITDA Run Rate en USD - usa arrays centralizados con cruce de año
  const ebitdaRunRateAnualUSD = useMemo(() => {
    let sumaDolares = 0;
    for (let offset = 0; offset < 3; offset++) {
      const mesRelativo = monthsToShow - 1 - offset;
      if (mesRelativo >= 0) {
        const ebitdaMes = ebitdaMensual[mesRelativo] || 0;
        const tc = dolarMensual[mesRelativo] || dolarFallback[pais] || 1;
        sumaDolares += ebitdaMes / tc;
      } else {
        const mesAnoAnterior = 12 + mesRelativo;
        const ebitdaMes = ebitdaMensualAnoAnterior[mesAnoAnterior] || 0;
        const tc = dolarMensualAnoAnterior[mesAnoAnterior] || dolarFallback[pais] || 1;
        sumaDolares += ebitdaMes / tc;
      }
    }
    return Math.round(sumaDolares * 4);
  }, [
    monthsToShow,
    ebitdaMensual,
    ebitdaMensualAnoAnterior,
    dolarMensual,
    dolarMensualAnoAnterior,
    dolarFallback,
    pais,
  ]);

  // Variación run rate EBITDA vs EBITDA últimos 12 meses
  const variacionVsPropioRitmo = useMemo(() => {
    if (ebitdaUltimos12Meses === 0) return null;
    const variacion = ((ebitdaRunRateAnual - ebitdaUltimos12Meses) / Math.abs(ebitdaUltimos12Meses)) * 100;
    return variacion;
  }, [ebitdaRunRateAnual, ebitdaUltimos12Meses]);

  // ARR Facturación para modo REGIÓN (en USD)
  const runRateAnualRegion = useMemo(() => {
    if (!modoCombinado || !publicidadMensualesCombinados || !hardwareMensualesCombinados) return 0;
    let suma = 0;
    for (let i = monthsToShow - 3; i < monthsToShow; i++) {
      if (i >= 0) {
        suma +=
          (publicidadMensualesCombinados[i] || 0) +
          (hardwareMensualesCombinados[i] || 0) +
          (gestionMensualesCombinados[i] || 0) +
          (btlMensualesCombinados[i] || 0);
      }
    }
    return Math.round(suma * 4);
  }, [
    modoCombinado,
    monthsToShow,
    publicidadMensualesCombinados,
    hardwareMensualesCombinados,
    gestionMensualesCombinados,
    btlMensualesCombinados,
  ]);

  // ARR EBITDA para modo REGIÓN (en USD)
  const ebitdaRunRateAnualRegion = useMemo(() => {
    if (!modoCombinado || !publicidadMensualesCombinados || !costosVariablesMensualesCombinados) return 0;
    let suma = 0;
    for (let i = monthsToShow - 3; i < monthsToShow; i++) {
      if (i >= 0) {
        const facturacion =
          (publicidadMensualesCombinados[i] || 0) +
          (hardwareMensualesCombinados[i] || 0) +
          (gestionMensualesCombinados[i] || 0) +
          (btlMensualesCombinados[i] || 0);
        const costosVar = costosVariablesMensualesCombinados[i] || 0;
        const costosFijos = costosFijosMensualesCombinados[i] || 0;
        suma += facturacion - costosVar - costosFijos;
      }
    }
    return Math.round(suma * 4);
  }, [
    modoCombinado,
    monthsToShow,
    publicidadMensualesCombinados,
    hardwareMensualesCombinados,
    gestionMensualesCombinados,
    btlMensualesCombinados,
    costosVariablesMensualesCombinados,
    costosFijosMensualesCombinados,
  ]);

  // Variación ARR región vs YTD anualizado región
  const variacionRunRateRegion = useMemo(() => {
    if (!modoCombinado) return null;
    const ytdFacturacionRegion =
      publicidadMensualesCombinados && hardwareMensualesCombinados
        ? publicidadMensualesCombinados.slice(0, monthsToShow).reduce((a, b) => a + b, 0) +
          hardwareMensualesCombinados.slice(0, monthsToShow).reduce((a, b) => a + b, 0) +
          (gestionMensualesCombinados?.slice(0, monthsToShow).reduce((a, b) => a + b, 0) || 0) +
          (btlMensualesCombinados?.slice(0, monthsToShow).reduce((a, b) => a + b, 0) || 0)
        : 0;
    const mesesTranscurridos = selectedYear === currentYear ? monthsToShow : 12;
    const ytdAnualizado = mesesTranscurridos > 0 ? Math.round((ytdFacturacionRegion * 12) / mesesTranscurridos) : 0;
    return ytdAnualizado > 0 ? (runRateAnualRegion / ytdAnualizado - 1) * 100 : 0;
  }, [
    modoCombinado,
    monthsToShow,
    selectedYear,
    currentYear,
    publicidadMensualesCombinados,
    hardwareMensualesCombinados,
    gestionMensualesCombinados,
    btlMensualesCombinados,
    runRateAnualRegion,
  ]);

  // Variación EBITDA ARR región vs YTD anualizado región
  const variacionEbitdaRunRateRegion = useMemo(() => {
    if (!modoCombinado || !costosVariablesMensualesCombinados || !costosFijosMensualesCombinados) return null;
    const ytdFacturacionRegion =
      (publicidadMensualesCombinados?.slice(0, monthsToShow).reduce((a, b) => a + b, 0) || 0) +
      (hardwareMensualesCombinados?.slice(0, monthsToShow).reduce((a, b) => a + b, 0) || 0) +
      (gestionMensualesCombinados?.slice(0, monthsToShow).reduce((a, b) => a + b, 0) || 0) +
      (btlMensualesCombinados?.slice(0, monthsToShow).reduce((a, b) => a + b, 0) || 0);
    const ytdCVRegion = costosVariablesMensualesCombinados.slice(0, monthsToShow).reduce((a, b) => a + b, 0);
    const ytdCFRegion = costosFijosMensualesCombinados.slice(0, monthsToShow).reduce((a, b) => a + b, 0);
    const ytdEbitdaRegion = ytdFacturacionRegion - ytdCVRegion - ytdCFRegion;
    const mesesTranscurridos = selectedYear === currentYear ? monthsToShow : 12;
    const ebitdaAnualizado = mesesTranscurridos > 0 ? Math.round((ytdEbitdaRegion * 12) / mesesTranscurridos) : 0;
    return ebitdaAnualizado !== 0
      ? ((ebitdaRunRateAnualRegion - ebitdaAnualizado) / Math.abs(ebitdaAnualizado)) * 100
      : null;
  }, [
    modoCombinado,
    monthsToShow,
    selectedYear,
    currentYear,
    publicidadMensualesCombinados,
    hardwareMensualesCombinados,
    gestionMensualesCombinados,
    btlMensualesCombinados,
    costosVariablesMensualesCombinados,
    costosFijosMensualesCombinados,
    ebitdaRunRateAnualRegion,
  ]);

  // Valores a mostrar según modo (región o país individual)
  const runRateParaMostrar = modoCombinado ? runRateAnualRegion : verEnDolares ? runRateAnualUSD : runRateAnual;
  const ebitdaRunRateParaMostrar = modoCombinado
    ? ebitdaRunRateAnualRegion
    : verEnDolares
      ? ebitdaRunRateAnualUSD
      : ebitdaRunRateAnual;
  const variacionFactParaMostrar = modoCombinado ? variacionRunRateRegion : null;
  const variacionEbitdaParaMostrar = modoCombinado ? variacionEbitdaRunRateRegion : variacionVsPropioRitmo;

  return {
    facturacionMensual,
    facturacionMensualAnoAnterior,
    costosVariablesMensual,
    costosVariablesMensualAnoAnterior,
    ebitdaMensual,
    ebitdaMensualAnoAnterior,
    hardwareMensualesCombinados,
    btlMensualesCombinados,
    depreciacionMensualesCombinados,
    ebitdaUltimos3,
    factUltimos3,
    runRateAnual,
    facturacionUltimos12Meses,
    ebitdaUltimos12Meses,
    ebitdaRunRateAnual,
    runRateAnualUSD,
    ebitdaRunRateAnualUSD,
    variacionVsPropioRitmo,
    runRateAnualRegion,
    ebitdaRunRateAnualRegion,
    variacionRunRateRegion,
    variacionEbitdaRunRateRegion,
    runRateParaMostrar,
    ebitdaRunRateParaMostrar,
    variacionFactParaMostrar,
    variacionEbitdaParaMostrar,
  };
}
